import { CloseOutlined } from '@ant-design/icons';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import commaNumber from 'comma-number';
import { Sparkles } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Divider, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import FadeDiv from '@/components/Common/FadeDiv';
import Sorter from '@/components/DataTable/Sorter';
import List from '@/components/List/List';
import Tooltip from '@/components/Tooltip/TooltipV2';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import { usePublic } from '@/contexts/PublicContext';
import type { AnalyzeChartFeedbackPeakParams } from '@/entities/chart';
import { getParentFieldName } from '@/features/(charts)/chart-controller/hooks/use-field-config';
import { buildFeedbackPeakAnalysisOptions } from '@/features/(charts)/chart-renderer/lib/build-feedback-peak-analysis-options';
import { PreprocessFactory } from '@/features/(charts)/chart-renderer/lib/ChartPreprocessFactory/ChartPreprocessFactory';
import { ChartFieldMap } from '@/features/(charts)/chart-renderer/model/chart-maps';
import { isTimeSeriesPeakAnalysisFilterSet } from '@/features/Feedback/components/FeedbackListModal/peak-analysis';
import { PeakAnalysisPanel } from '@/features/Feedback/components/FeedbackListModal/peak-analysis-panel';
import { usePeakAnalysis } from '@/features/Feedback/components/FeedbackListModal/use-peak-analysis';
import { FEEDBACK_SORT_OPTIONS } from '@/features/Feedback/configs/feedback-sort-options';
import { useFeedbackSortState } from '@/features/Feedback/hooks/useFeedbackSortState';
import useGlobalSearchModalAction from '@/features/GlobalSearch/hooks/useGlobalSearchModalAction';
import FeedbackCard from '@/features/Taxonomy/components/FeedbackCard/FeedbackCard';
import useKeymap from '@/hooks/pure/useKeymap';
import service from '@/services/index';
import { Button as ShadcnButton } from '@/shared/ui';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
  type DetailModalOptions,
} from '@/stores/pure/DetailModalStore';
import { usePopup } from '@/stores/pure/PopupStore';
import { detailModalStyle } from '@/styles/style';
import { track } from '@/utils/track/track';

type Props = {
  query: string;
  pos: number;
  options: DetailModalOptions<
    ChartFeedbackListModalOptions<CellFeedbackListModalOptions>
  >;
  closeModal: () => void;
  onSelectNewModal: (alias: string, type: DETAIL_MODAL_TYPE) => void;
};

const ChartSegmentFeedbackListModal = (props: Props) => {
  const isPublic = usePublic();
  const { workspace_id } = useParams();
  const { toDetailPage } = useGlobalSearchModalAction();
  const scrollRef = useRef<HTMLDivElement>(null);
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const setPopup = usePopup();
  const [sortStatus, setSortStatus] = useFeedbackSortState();
  const [selectedFeedback, setSelectedFeedback] =
    useState<SelectedFeedbackState>(null);
  const peakAnalysisOptions = useMemo(
    () =>
      buildFeedbackPeakAnalysisOptions(props.options.chart, {
        autoStart: props.options.startPeakAnalysisOnFeedbackOpen,
        dateRangeOverride: props.options.peakAnalysisDateRangeOverride,
      }),
    [
      props.options.chart,
      props.options.peakAnalysisDateRangeOverride,
      props.options.startPeakAnalysisOnFeedbackOpen,
    ],
  );
  const peakAnalysisRequestParams = useMemo<AnalyzeChartFeedbackPeakParams>(
    () => ({
      chart: new PreprocessFactory(props.options.chart).runPreprocess(),
      cell: props.options.cell,
    }),
    [props.options.cell, props.options.chart],
  );
  const showPeakAnalysisButton =
    isTimeSeriesPeakAnalysisFilterSet(peakAnalysisOptions);
  const { isPeakAnalysisLoading, peakAnalysisState, startPeakAnalysis } =
    usePeakAnalysis(peakAnalysisOptions);
  const startChartCellPeakAnalysis = useCallback(() => {
    if (!workspace_id) return;

    void startPeakAnalysis({
      type: 'chart-cell',
      workspaceAlias: workspace_id,
      params: peakAnalysisRequestParams,
    });
  }, [peakAnalysisRequestParams, startPeakAnalysis, workspace_id]);

  const fetchFeedbackList = async (
    pageParam: null | string,
    sortStatus: NotNullableSortStatusT,
  ) => {
    if (!workspace_id) throw new Error('workspace_id is required');
    const newChartParams = new PreprocessFactory(
      props.options.chart,
    ).runPreprocess();

    const data = await service.chart.fetchChartFeedbackList(
      workspace_id,
      {
        ...props.options,
        chart: newChartParams,
        paginate_token: pageParam || undefined,
      },
      sortStatus,
      isPublic,
    );

    const feedbacks = data.feedbacks || [];

    return {
      count: data.count,
      list: feedbacks,
      paginate_token: feedbacks[feedbacks.length - 1]?.paginate_token || null,
    };
  };

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: [
        'chart-raw-data-list',
        JSON.stringify(props.options),
        sortStatus.sortBy,
        sortStatus.orderStatus,
      ],
      queryFn: ({ pageParam }) => fetchFeedbackList(pageParam, sortStatus),
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => lastPage?.paginate_token,
      placeholderData: keepPreviousData,
    });

  const onFeedbackItemClick = (state: SelectedFeedbackState) => {
    if (!state) return;
    const openFeedbackModal = () => {
      if (isPublic) {
        setNewModal({
          alias: state.feedbackAlias,
          type: DETAIL_MODAL_TYPE.STATIC_FEEDBACK,
          idx: 1,
          keys: [state.blockAlias],
          options: {
            focusedBlock: state.blockAlias,
          },
        });
      } else {
        setNewModal({
          alias: state.feedbackAlias,
          type: DETAIL_MODAL_TYPE.FEEDBACK,
          idx: 1,
          keys: [state.blockAlias],
          options: {
            focusedBlock: state.blockAlias,
          },
        });
      }
    };
    if (selectedFeedback?.feedbackAlias === state.feedbackAlias) {
      if (selectedFeedback?.blockAlias === state.blockAlias) {
        setSelectedFeedback(null);
      } else {
        setSelectedFeedback(state);
        openFeedbackModal();
      }
    } else {
      setSelectedFeedback(state);
      openFeedbackModal();
    }
  };

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: 0,
    });
  }, [props.query]);

  useEffect(() => {
    if (
      !showPeakAnalysisButton ||
      !peakAnalysisOptions?.autoStart ||
      peakAnalysisState.status !== 'idle'
    ) {
      return;
    }

    startChartCellPeakAnalysis();
  }, [
    peakAnalysisOptions?.autoStart,
    peakAnalysisState.status,
    showPeakAnalysisButton,
    startChartCellPeakAnalysis,
  ]);

  const flattenData = useMemo(() => {
    return data?.pages
      .flat()
      .map((i) => i.list)
      .flat();
  }, [data?.pages]);

  const total = useMemo(() => {
    return data?.pages[0].count || 0;
  }, [data?.pages]);

  const xDomain = getParentFieldName(props.options.chart.field.type);
  const xField = ChartFieldMap.get(props.options.chart.field.type);

  const targetSeries =
    props.options.chart.series[props.options.cell.series_index];

  const yDomain = targetSeries?.cross_field
    ? getParentFieldName(targetSeries?.cross_field.type)
    : null;

  const yField = targetSeries?.cross_field
    ? ChartFieldMap.get(targetSeries?.cross_field.type)
    : null;

  const onCloseClick = () => {
    if (isPeakAnalysisLoading) {
      setPopup({
        type: 'confirm',
        popupTheme: 'negative',
        title: 'Stop analysis and close?',
        message:
          'Closing this window will hide the in-progress analysis result.',
        confirmText: 'Close',
        cancelText: 'Keep analyzing',
        onConfirm: () => {
          props.closeModal();
        },
      });
      return;
    }

    props.closeModal();
  };

  const onSearchClick = async () => {
    // Track view insight click event
    track(AMP_KEYS.CHART_VIEW_INSIGHT_CLICK, {
      chart_alias: props.query,
      chart_type: props.options.chart.type,
    });

    const res = await service.chart.createSearchWithChart(props.options);
    props.closeModal();
    toDetailPage(res.alias);
  };

  useKeymap('Escape', onCloseClick);

  return (
    <ModalStyle status={true} pos={props.pos}>
      <ModalContent f={1}>
        <Flex j="flex-end" a="center" style={{ padding: '16px 32px' }}>
          <Flex g="6px">
            <IconButton
              icon={<CloseOutlined />}
              onClick={onCloseClick}
              tooltip="Close"
              shortcut="ESC"
            />
          </Flex>
        </Flex>
        <Divider />
        <Space h={24} />
        <Flex j="space-between" a="center" g={24} padding="0px 32px">
          <Flex g={16} wrap="wrap">
            <Tooltip tooltipNode={props.options.cell.item_x_name}>
              <VFlex g={4}>
                <Typo type="label3" weight="m" className="text-gray-600">
                  {xDomain} / {xField}
                </Typo>
                <Typo
                  type="label1"
                  weight="m"
                  className="text-gray-800"
                  ellipsis
                  style={{ maxWidth: 200 }}
                >
                  {props.options.cell.item_x_name}
                </Typo>
              </VFlex>
            </Tooltip>

            {targetSeries.cross_field && (
              <Tooltip tooltipNode={props.options.cell.item_y_name}>
                <VFlex g={4}>
                  <Typo type="label3" weight="m" className="text-gray-600">
                    {yDomain} / {yField}
                  </Typo>
                  <Typo
                    type="label1"
                    weight="m"
                    className="text-gray-800"
                    ellipsis
                    style={{ maxWidth: 200 }}
                  >
                    {props.options.cell.item_y_name}
                  </Typo>
                </VFlex>
              </Tooltip>
            )}
            {props.options.cell.item_z && (
              <Tooltip tooltipNode={props.options.cell.item_z_name}>
                <VFlex g={4}>
                  <Typo type="label3" weight="m" className="text-gray-600">
                    Feedback / Sentiment
                  </Typo>
                  <Typo
                    type="label1"
                    weight="m"
                    className="text-gray-800"
                    ellipsis
                    style={{ maxWidth: 200 }}
                  >
                    {props.options.cell.item_z_name}
                  </Typo>
                </VFlex>
              </Tooltip>
            )}
          </Flex>
          {(showPeakAnalysisButton || !isPublic) && (
            <Flex g="6px" a="center">
              {showPeakAnalysisButton && (
                <ShadcnButton
                  type="button"
                  variant="outline"
                  size="xs"
                  disabled={
                    isPeakAnalysisLoading ||
                    peakAnalysisState.status === 'success'
                  }
                  onClick={startChartCellPeakAnalysis}
                  className="border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary"
                >
                  <Sparkles className="size-4" />
                  Analyze Change Drivers
                </ShadcnButton>
              )}
              {!isPublic && (
                <Button variant="primary" sizeType="s" onClick={onSearchClick}>
                  View insight
                </Button>
              )}
            </Flex>
          )}
        </Flex>
        <Space h={24} />
        <Flex padding="0px 32px" j="space-between" a="center">
          <Typo type="label2" className="text-gray-700">
            Total feedback : {commaNumber(total)}
          </Typo>
          <Sorter
            options={FEEDBACK_SORT_OPTIONS}
            currSortStatus={sortStatus}
            onSortChange={setSortStatus}
          />
        </Flex>
        <ScrollBody ref={scrollRef}>
          <Space h={24} />
          {peakAnalysisState.status !== 'idle' && (
            <>
              <div className="px-8">
                <PeakAnalysisPanel
                  state={peakAnalysisState}
                  onRetry={startChartCellPeakAnalysis}
                />
              </div>
              <Space h={12} />
            </>
          )}
          <List
            isLoading={isLoading}
            data={flattenData}
            className="gap-2 pr-4 pl-8 pb-6 h-auto overflow-visible"
            render={(f) => (
              <FeedbackCard
                selectedFeedback={selectedFeedback}
                selectedUnit={null}
                setSelectedFeedback={onFeedbackItemClick}
                key={f.alias}
                feedback={f}
              />
            )}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        </ScrollBody>
      </ModalContent>
    </ModalStyle>
  );
};

const ModalContent = styled(VFlex)`
  height: 100%;
  min-height: 0;
`;

const ScrollBody = styled(VFlex)`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const ModalStyle = styled(FadeDiv)<{ pos: number }>`
  position: fixed;
  width: 600px;
  height: 100vh;
  top: 0;
  right: ${({ pos }) => pos}px;
  ${detailModalStyle};
`;
export default ChartSegmentFeedbackListModal;
